import {
  Box,
  Button,
  Modal,
  ModalBody,
  ModalHeader,
  ModalFooter,
  Text,
  OTPInput,
  Link,
  ArrowUpRightIcon,
} from '@razorpay/blade/components';
import useTwoFactorAuth from 'hooks/useTwoFactorAuth';
import React, { useEffect, useRef, useState } from 'react';
import { OTP_REGEX } from 'utils/regexes';
import { BankAccountFormData } from './types';
import { bankProofTypeToLabel } from '../data';
import { AppError, getAppError } from 'utils/AppError';
import { OTP_VERIFICATION_ERROR } from 'constants/otp';

export const OtpModal = ({
  isOpen,
  onDismiss,
  onResendOtp,
  onContinue,
  isLoading,
  shouldRequireApproval,
  bankAccountDetails,
  skipPennyTestingAndApproval,
  otpError
}: {
  isOpen: boolean;
  onDismiss: () => void;
  onResendOtp: () => void;
  onContinue: (otp: string) => void;
  isLoading: boolean;
  shouldRequireApproval: boolean;
  bankAccountDetails?: Partial<BankAccountFormData>;
  skipPennyTestingAndApproval: boolean;
  otpError: typeof AppError | null;
}) => {
  const [otp, setOtp] = useState('');
  const otpRef = useRef<HTMLInputElement[]>(null);
  const { isAuthenticatorAppEnabled, email: loggedInUserEmail } = useTwoFactorAuth();

  const shouldShowError = otpError
    ? getAppError(otpError).getDetails().code === OTP_VERIFICATION_ERROR
    : false;

  const onOtpChange = ({ value }: { value?: string }) => {
    if (OTP_REGEX.test(value ?? '')) {
      setOtp(value ?? '');
    } else {
      setOtp('');
    }
  };

  useEffect(() => {
    if (!isOpen) {
      setOtp('');
    }
  }, [isOpen]);

  if (!bankAccountDetails) {
    return null;
  }
  const { accountNumber, ifscCode, beneficiaryName, bankProofFile, bankProofType } =
    bankAccountDetails;

  const handleContinue = () => {
    if (bankAccountDetails && otp.length === 6) {
      onContinue(otp);
    }
  };

  const handleResendOtp = () => {
    onResendOtp();
    setOtp('');
    otpRef.current?.[0].focus();
  };

  return (
    <Modal
      isOpen={isOpen}
      onDismiss={() => {
        if (!isLoading) {
          onDismiss();
        }
      }}
      size="small">
      <ModalHeader
        title={
          skipPennyTestingAndApproval ? 'Update without verification?' : 'Verify with OTP'
        }
      />
      <ModalBody>
        {skipPennyTestingAndApproval && (
          <Text marginBottom="spacing.6" color="surface.text.gray.muted">
            Are you sure you want to skip penny testing and update the bank account details anyway?
            To continue, enter the OTP.
          </Text>
        )}
        <Box
          borderColor="surface.border.gray.muted"
          borderWidth="thin"
          borderRadius="large"
          marginBottom="spacing.8"
          overflow="hidden">
          <Box
            padding="spacing.5"
            backgroundColor="surface.background.gray.moderate"
            display="flex"
            alignItems="center"
            gap="spacing.3">
            <Box
              padding="spacing.2"
              backgroundColor="surface.background.gray.intense"
              borderRadius="small">
              <ArrowUpRightIcon color="surface.icon.gray.muted" size="small" />
            </Box>
            <Text color="surface.text.gray.normal" weight="semibold">
              Bank Account Details
            </Text>
          </Box>
          <Box display="flex" flexDirection="column" gap="spacing.5" padding="spacing.4">
            <Box display="flex" gap="spacing.5">
              <Box width="50%" display="flex" flexDirection="column" gap="spacing.2">
                <Text color="surface.text.gray.muted" weight="regular">
                  Account Number
                </Text>
                <Text color="surface.text.gray.subtle" weight="regular">
                  {accountNumber}
                </Text>
              </Box>

              <Box display="flex" flexDirection="column" gap="spacing.2">
                <Text color="surface.text.gray.muted" weight="regular">
                  IFSC Code
                </Text>
                <Text color="surface.text.gray.subtle" weight="regular">
                  {ifscCode}
                </Text>
              </Box>
            </Box>
            {Boolean(beneficiaryName || bankProofType || bankProofFile) && (
              <Box display="flex" gap="spacing.5">
                {Boolean(beneficiaryName) && (
                  <Box width="50%" display="flex" flexDirection="column" gap="spacing.2">
                    <Text color="surface.text.gray.muted" weight="regular">
                      Account Holder Name
                    </Text>
                    <Text color="surface.text.gray.subtle" weight="regular">
                      {beneficiaryName}
                    </Text>
                  </Box>
                )}
                {Boolean(bankProofType) && Boolean(bankProofFile) && (
                  <Box display="flex" flexDirection="column" gap="spacing.2">
                    <Text color="surface.text.gray.muted" weight="regular">
                      Uploaded Proof
                    </Text>
                    <Text color="surface.text.gray.subtle" weight="regular">
                      {bankProofTypeToLabel[bankProofType as keyof typeof bankProofTypeToLabel]}
                    </Text>
                  </Box>
                )}
              </Box>
            )}
          </Box>
        </Box>
        <OTPInput
          validationState={shouldShowError ? 'error' : 'none'}
          errorText={`The ${
            !isAuthenticatorAppEnabled ? 'OTP' : 'authentication code'
          } entered is incorrect. Please try again.`}
          key={otp}
          ref={otpRef}
          onChange={onOtpChange}
          autoFocus={true}
          helpText={
            isAuthenticatorAppEnabled
              ? 'Open the two-factor authenticator app on your mobile device to view the authentication code'
              : `One Time Password sent to ${loggedInUserEmail}`
          }
          label={isAuthenticatorAppEnabled ? 'Enter authentication code' : 'Enter OTP'}
          otpLength={6}
          isDisabled={isLoading}
          value={otp}
        />
        <Box marginTop="spacing.3" display="flex" alignItems="center" gap="spacing.1">
          <Text size="small" weight="semibold" color="surface.text.gray.muted">
            Didn’t receive OTP?
          </Text>
          <Link variant="button" onClick={handleResendOtp} isDisabled={isLoading} size="small">
            Resend
          </Link>
        </Box>
      </ModalBody>
      <ModalFooter>
        <Box display="flex" justifyContent="flex-end">
          <Button isDisabled={!(otp.length === 6)} isLoading={isLoading} onClick={handleContinue}>
            {shouldRequireApproval ? 'Request Approval' : 'Continue'}
          </Button>
        </Box>
      </ModalFooter>
    </Modal>
  );
};
